import { Fragment, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useDispatch, useSelector } from 'react-redux';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import './style.css';
import { MODAL_TOGGLE } from '../../redux/actions/app';
import { useSnackbar } from 'notistack';
import { ID, MAX_SUMMARY_LENGTH, VALID_DOMAINS } from './config';
import { CURRENT_NODE, NODE_EVIDENCE } from '../../redux/actions/board';
import { Link } from 'react-router-dom';
import { PATH_ABOUT } from '../../router/config';
import AlertTitle from '@mui/material/AlertTitle';
import Divider from '@mui/material/Divider';

const ManageEvidence = () => {

    const dispatch = useDispatch(),
    { enqueueSnackbar } = useSnackbar(),
    labels = useMemo( () => new LocalizedStrings( LOCALIZATIONS ), []),
    { locale, modal } = useSelector( state => state.app ),
    [ evidence, setEvidence ] = useState([]),
    currentNode = useSelector( state => state.board.currentNode ),
    open = Boolean( modal === ID );

    /**
     * @name addData
     * @function
     * @description Adds a new evidence item
     */
    const addData = () => {
        const tempEv = [ ...evidence ];
        tempEv.push({});
        setEvidence([ ...tempEv ]);
    }

    /**
     * @name cancel
     * @function
     * @description Hides the modal
     */
    const cancel = () => {
        dispatch({ type: MODAL_TOGGLE });
        dispatch({ type: CURRENT_NODE });
        setEvidence([]);
    }

    /**
     * @name commit
     * @function
     * @description Commits changes to the context node
     */
    const commit = () => {
        let badData = false,
        noData = false;

        for( let i = 0; i < evidence.length; ++i ){
            if( badData || noData ) break;
            const item = evidence[ i ];
            badData = Boolean( item.badData );
            noData = !( item.url?.length > 0 || item.summary?.length > 0 );
        }

        // commit data if its good
        if( !badData && !noData ){
            dispatch({ type: NODE_EVIDENCE, payload: { id: currentNode.id, evidence: [ ...evidence ] } });
            cancel();
        
        // show the user an error
        } else {
            enqueueSnackbar(
                labels[ ( badData ) ? 'failBadData' : 'failNoData' ],
                { variant: 'error' }
            );
        }
    }

    /**
     * @name onChange
     * @function
     * @description
     * @param {number} index 
     * @returns {function}
     */
    const onChange = index => ({ target }) => {
        const tempEv = [ ...evidence ];
        tempEv[ index ][ target.name ] = target.value;
        if( target.name === 'url' ){

            // assume its bad to begin with so we don't have crazy nested elses
            tempEv[ index ].badData = true;

            // validate the URL being entered
            if( target.validity.valid ){
                const checkDomain = new URL( target.value );
                if( VALID_DOMAINS.includes( checkDomain.host ) ){
                    delete tempEv[ index ].badData;
                }                
            }
            
        }
        setEvidence([ ...tempEv ]);
    }

    /**
     * @name remove
     * @function
     * @description Removes a piece of evidence
     * @param {number} index Evidence to remove
     * @returns {function}
     */
    const remove = index => e => {
        const tempEv = [ ...evidence ];
        tempEv.splice( index, 1 );
        setEvidence([ ...tempEv ]);
    }

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    // set initial data
    useEffect(() => {
        if( currentNode.id ) setEvidence([ ...currentNode?.evidence ]);
    }, [ currentNode, setEvidence ]);

    return (
        <Dialog open={ open } onClose={ cancel }>
            <DialogTitle>
                { 
                    labels.formatString(
                        labels.title,
                        currentNode?.name || ''
                    )
                }
            </DialogTitle>
            <DialogContent>
                <Stack>
                    <Alert severity="info">
                        <AlertTitle>{ labels.alertTitle }</AlertTitle>
                        { 
                            labels.formatString(
                                labels.note,
                                <>
                                    <Link to={ PATH_ABOUT } className="MuiTypography-root MuiTypography-inherit MuiLink-root MuiLink-underlineAlways css-14b5yu0-MuiTypography-root-MuiLink-root">{ labels.about }</Link>
                                </>
                            )
                        }
                    </Alert>                    
                    {
                        evidence.map(
                            ( item, index ) => (
                                <Fragment key={ 'evidence-' + index }>
                                    <Stack direction="row">
                                        <Stack sx={{ width: '100%' }}>
                                            <FormControl fullWidth >
                                                <TextField
                                                    error={ item.badData } 
                                                    label={ labels.url }
                                                    name="url"
                                                    onChange={ onChange( index ) }
                                                    required
                                                    type="url"
                                                    value={ item.url || '' } />
                                                {
                                                    item.badData ?
                                                        <FormHelperText error>{ labels.badUrl }</FormHelperText> :
                                                        null
                                                }
                                                <FormHelperText>
                                                    { labels.allowedDomains }
                                                    { VALID_DOMAINS.join( ', ' ) }
                                                </FormHelperText>
                                            </FormControl>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label={ labels.explanation }
                                                    inputProps={{ maxLength: MAX_SUMMARY_LENGTH }}
                                                    multiline
                                                    name="summary"
                                                    onChange={ onChange( index ) }
                                                    required
                                                    value={ item.summary || '' } />
                                                <FormHelperText>
                                                    { 
                                                        labels.formatString(
                                                            labels.maxLength,
                                                            item.summary?.length || 0
                                                        )
                                                    }
                                                </FormHelperText>
                                            </FormControl>
                                        </Stack>
                                        <Button aria-label="Remove" color="error" onClick={ remove( index ) }>
                                            <RemoveCircleOutlineIcon />
                                        </Button>
                                    </Stack>
                                    {
                                        ( index + 1 ) < evidence.length ?
                                            <Divider /> :
                                            null
                                    }
                                </Fragment>
                            )
                        )
                    }
                    <Button aria-label={ labels.addEvidence } onClick={ addData } startIcon={ <AddCircleOutlineIcon /> }>
                        { labels.addEvidence }
                    </Button> 
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button aria-label={ labels.commit } onClick={ commit } variant="contained" startIcon={ <CheckCircleOutlineIcon /> }>
                    { labels.commit }
                </Button>
                <Button aria-label={ labels.cancel } variant="outlined" onClick={ cancel } startIcon={ <NotInterestedIcon /> }>
                    { labels.cancel }
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ManageEvidence;